export const namespaced = true;

const PROFILE_KEY = "google_oauth_profile";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** True if `label` is clearly a phone (or matches stored mobile), not a person's name. */
function isPhoneLikeLabel(label, mobileDigits) {
  if (!label || typeof label !== "string") return false;
  const trimmed = label.trim();
  const labelDigits = trimmed.replace(/\D/g, "");
  const mob = String(mobileDigits || "").replace(/\D/g, "");
  if (mob.length >= 10 && (labelDigits === mob || labelDigits.endsWith(mob))) {
    return true;
  }
  if (/^\+91\s*\d{10}$/.test(trimmed.replace(/\s/g, ""))) return true;
  if (labelDigits.length === 10 && /^\d{10}$/.test(labelDigits)) return true;
  return false;
}

/** First email-looking string found anywhere in a JSON-like object (shallow walk). */
function findEmailDeep(obj, depth = 0) {
  if (!obj || typeof obj !== "object" || depth > 6) return "";
  for (const key of Object.keys(obj)) {
    const v = obj[key];
    if (typeof v === "string" && EMAIL_RE.test(v.trim())) return v.trim();
    if (v && typeof v === "object" && !Array.isArray(v)) {
      const found = findEmailDeep(v, depth + 1);
      if (found) return found;
    }
    if (Array.isArray(v)) {
      for (const item of v) {
        if (typeof item === "string" && EMAIL_RE.test(item.trim())) return item.trim();
        if (item && typeof item === "object") {
          const found = findEmailDeep(item, depth + 1);
          if (found) return found;
        }
      }
    }
  }
  return "";
}

/** Known shapes for verify-otp and profile APIs (email often nested). */
function pickEmailFromPayload(d) {
  if (!d || typeof d !== "object") return "";
  const tryStr = (v) =>
    v && typeof v === "string" && EMAIL_RE.test(v.trim()) ? v.trim() : "";
  const candidates = [
    d.email,
    d.registered_email,
    d.user_email,
    d.borrower_email,
    d.contact_email,
    d.official_email,
    d.personal_email,
    d.google_email,
    d.social_email,
    d.username,
    d.user && d.user.email,
    d.result && d.result.email,
    d.result && d.result.user && d.result.user.email,
    d.result && d.result.user_email,
    d.data && d.data.email,
    d.profile && d.profile.email,
    d.user_details && d.user_details.email,
    d.user_profile && d.user_profile.email,
    d.payload && d.payload.email,
    d.borrower && d.borrower.email,
    d.customer && d.customer.email,
    d.details && d.details.email,
  ];
  for (const c of candidates) {
    const e = tryStr(c);
    if (e) return e;
  }
  return findEmailDeep(d);
}

export const state = {
  token: null,
  profile: null,
};

export const mutations = {
  SET_TOKEN(state, token) {
    state.token = token;
  },
  SET_PROFILE(state, profile) {
    state.profile = profile;
  },
  CLEAR(state) {
    state.token = null;
    state.profile = null;
  },
};

export const actions = {
  hydrateFromStorage({ commit }) {
    const token = localStorage.getItem("token") || null;
    let profile = null;
    // Only mirror profile into Vuex when logged in. `google_oauth_profile` stays in
    // localStorage across logout so returning users keep Google email/name for UI after OTP.
    if (token) {
      try {
        profile = JSON.parse(localStorage.getItem(PROFILE_KEY) || "null");
      } catch {
        profile = null;
      }
      if (profile && isPhoneLikeLabel(profile.name, profile.mobile)) {
        profile = { ...profile, name: "User" };
        localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
      }
    }
    commit("SET_TOKEN", token);
    commit("SET_PROFILE", profile);
  },

  setSession({ commit }, { token, profile }) {
    if (token) {
      commit("SET_TOKEN", token);
    }
    let p = profile;
    if (p == null || p === undefined) {
      try {
        p = JSON.parse(localStorage.getItem(PROFILE_KEY) || "null");
      } catch {
        p = null;
      }
    }
    if (p != null && typeof p === "object") {
      commit("SET_PROFILE", p);
    }
  },

  /** After OTP: force Vuex profile from localStorage (fixes mobile-only login UI). */
  pullProfileFromLocalStorage({ commit }) {
    if (!localStorage.getItem("token")) return;
    let p = null;
    try {
      p = JSON.parse(localStorage.getItem(PROFILE_KEY) || "null");
    } catch {
      p = null;
    }
    if (p != null && typeof p === "object") {
      commit("SET_PROFILE", p);
    }
  },

  /**
   * Merge mobile (and optional Google / form fields) into google_oauth_profile.
   * Use for every path that sends OTP (USER_LOGIN, PL_LOGIN, PL_SEND_OTP).
   */
  syncStoredProfileWithMobile({ commit, state }, payload) {
    const mobile = payload && payload.mobile;
    const google = (payload && payload.google) || null;
    const formFields = (payload && payload.formFields) || {};
    let existing = null;
    try {
      existing = JSON.parse(localStorage.getItem(PROFILE_KEY) || "null");
    } catch {
      existing = null;
    }
    const e = existing || {};
    const g = google || {};
    const digits = String(mobile || "").replace(/\D/g, "");
    const formName = [formFields.first_name, formFields.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();
    const merged = {
      given_name: g.given_name || formFields.first_name || e.given_name || "",
      family_name: g.family_name || formFields.last_name || e.family_name || "",
      name: g.name || formName || e.name || "",
      email: g.email || formFields.email || e.email || "",
      picture: g.picture || e.picture || "",
      sub: g.sub || e.sub || "",
      mobile: digits || e.mobile || "",
    };
    localStorage.setItem(PROFILE_KEY, JSON.stringify(merged));
    if (state.token || localStorage.getItem("token")) {
      commit("SET_PROFILE", merged);
    }
  },

  setProfile({ commit }, profile) {
    commit("SET_PROFILE", profile);
  },

  /** Merge server profile fields (e.g. from GET_PROFILE_STATUS) into localStorage + Vuex. */
  mergeProfileFromApi({ commit, state }, apiData) {
    if (!apiData || typeof apiData !== "object") return;
    let existing = state.profile;
    if (!existing) {
      try {
        existing = JSON.parse(localStorage.getItem(PROFILE_KEY) || "null");
      } catch {
        existing = null;
      }
    }
    const e = existing || {};
    const pickEmail = (d) => pickEmailFromPayload(d) || "";
    const pickFirst = (d) =>
      d.first_name || (d.user && d.user.first_name) || "";
    const pickLast = (d) =>
      d.last_name || (d.user && d.user.last_name) || "";
    const pickName = (d) => {
      const n =
        d.name ||
        d.full_name ||
        (d.user && d.user.name) ||
        [pickFirst(d), pickLast(d)].filter(Boolean).join(" ").trim();
      return n || "";
    };
    const pickPic = (d) =>
      d.picture ||
      d.profile_picture ||
      d.avatar ||
      (d.user && d.user.picture) ||
      "";

    const email = pickEmail(apiData) || e.email || "";
    const given = pickFirst(apiData) || e.given_name || "";
    const family = pickLast(apiData) || e.family_name || "";
    const picture = pickPic(apiData) || e.picture || "";
    const mobile =
      e.mobile ||
      apiData.mobile ||
      (apiData.user && apiData.user.mobile) ||
      (apiData.result && apiData.result.mobile) ||
      "";

    const resolvedName =
      pickName(apiData) ||
      (given || family ? `${given} ${family}`.trim() : "") ||
      "";
    const keepExistingName =
      e.name &&
      !isPhoneLikeLabel(e.name, mobile) &&
      !EMAIL_RE.test(String(e.name).trim())
        ? e.name
        : "";
    const displayName =
      resolvedName ||
      keepExistingName ||
      (email ? email.split("@")[0] : "") ||
      "User";

    const merged = {
      given_name: given || e.given_name || "",
      family_name: family || e.family_name || "",
      name: displayName,
      email,
      picture: picture || e.picture || "",
      sub: e.sub || "",
      mobile,
    };
    localStorage.setItem(PROFILE_KEY, JSON.stringify(merged));
    commit("SET_PROFILE", merged);
  },

  logout({ commit, dispatch }) {
    localStorage.removeItem("token");
    localStorage.removeItem("otp-token");
    localStorage.removeItem("pl_token");
    // Business-Loan session (set during the BL login/OTP flow).
    sessionStorage.removeItem("access_token");
    localStorage.removeItem("bl_access_token");
    localStorage.removeItem("bl_login_mobile");
    localStorage.removeItem("bl_is_already_exist");
    // Keep google_oauth_profile in localStorage so email/name return after mobile re-login.
    commit("CLEAR");
    dispatch("personal_loan/setMobileNumber", "", { root: true });
    dispatch("personal_loan/setShowRegisterForm", false, { root: true });
    dispatch("personal_loan/setStepper", 1, { root: true });
  },
};

export const getters = {
  /** Prefer Vuex token; fall back to localStorage so navbar matches PL flows if store desyncs. */
  isLoggedIn: (state) =>
    !!(state.token && String(state.token).trim()) ||
    !!localStorage.getItem("token") ||
    // Business-Loan session token (set at BL OTP verify). The sessionStorage copy
    // is cleared when leaving offers, so also honour the persistent localStorage
    // copy — keeps the navbar logged-in across refresh, like PL's token.
    !!sessionStorage.getItem("access_token") ||
    !!localStorage.getItem("bl_access_token"),
  displayName: (state) => {
    if (!state.profile) return "";
    const n = state.profile.name;
    const mob = state.profile.mobile;
    if (!n) return "User";
    if (isPhoneLikeLabel(n, mob)) return "User";
    return n;
  },
  email: (state) => (state.profile ? state.profile.email : ""),
  mobile: (state) => (state.profile ? state.profile.mobile : ""),
  avatarUrl: (state) => (state.profile ? state.profile.picture : ""),
  profile: (state) => state.profile,
};
